import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import queryString from 'query-string';

import { useAppDispatch } from '../../../stores/redux.store';
import { resetLoginForm } from '../../../actions/login-actions';
import Bull from '../../../components/BulletSeparator';
import ButtonLink from '../../../components/ButtonLink';
import { OidcContext, OidcDisplay, URLParameters } from '../../../types';
import { isOidcDisplayMode } from '../../../utilities/oidc/oidc';
import { useAnalyticsClient } from '../../../utilities/analytics/analytics-web-client';
import { getCurrentCloudType } from '../../../utilities/env';

import { LoginPageMessages } from '../messages';
import { loginPageId } from '../constants';
import { ResetPasswordLink } from './ResetPasswordLink';

const WhiteboxFooter = ({
  isMobileApp,
  oidcContext,
  showSocialLoginOnly,
}: {
  isMobileApp: boolean;
  oidcContext: OidcContext | undefined;
  showSocialLoginOnly: boolean | null;
}) => {
  const dispatch = useAppDispatch();
  const analyticsClient = useAnalyticsClient();

  const isCommercial = getCurrentCloudType() === 'commercial';

  const queryParams: URLParameters = queryString.parse(location.search);

  const signupLinkEnabled = useMemo(() => {
    // Due to Apple Store rules, we don't want sign up link on login page in Mobile OAuth case, unless we provide redirection from login to signup
    const shouldRenderSignupLink =
      isCommercial &&
      (!isMobileApp || isOidcDisplayMode(oidcContext, OidcDisplay.LoginWithRedirect));

    return queryParams.shide !== 'true' && shouldRenderSignupLink;
  }, [isCommercial, isMobileApp, oidcContext, queryParams.shide]);

  const getSignupLinkElement = (): React.ReactNode => {
    if (signupLinkEnabled) {
      const elementText = <FormattedMessage {...LoginPageMessages.createAnAccount} />;

      return (
        <Link
          to={'/signup' + location.search}
          id="signup"
          className="primary-action"
          onClick={() => {
            analyticsClient.linkClickedEvent(loginPageId, 'signUpLink');
          }}>
          {elementText}
        </Link>
      );
    }

    return null;
  };

  const signupLink = getSignupLinkElement();

  if (showSocialLoginOnly) {
    return (
      <ButtonLink
        id="use-a-different-login-method"
        spacing="none"
        onClick={() => {
          dispatch(resetLoginForm());
        }}>
        <FormattedMessage {...LoginPageMessages.socialFormUseDifferentLoginMethod} />
      </ButtonLink>
    );
  }

  return (
    <ul>
      <li>
        <ResetPasswordLink>
          <FormattedMessage id="trouble.logging.in" defaultMessage="Can't log in?" />
        </ResetPasswordLink>
      </li>
      {signupLink && (
        <>
          <Bull />
          <li>{signupLink}</li>
        </>
      )}
    </ul>
  );
};

export default WhiteboxFooter;
